import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import globalStyles from "../../../global";

const NavbarSr = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL;
  const {  i18n } = useTranslation();

  // Get the current language from i18next
  const currentLanguage = i18n.language;

  // Determine if the language is RTL based on i18next's language code
  const isRTL = currentLanguage === "ar";

  // Sri Lanka navigation items data - using translation keys
  const navItems = [
    { path: "/srilanka/services", label: "Services" },
    { path: "/srilanka/pricing", label: "Pricing" },
    { path: "/srilanka/locateus", label: "Locate Us" },
    { path: "/srilanka/careers", label: "Careers" },
  ];

  useEffect(() => {
    // Set active link based on current path
    setActiveLink(window.location.pathname);

    // Add scroll event listener
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (path) => {
    setActiveLink(path);
    setMobileOpen(false);
  };



  const renderDesktopNav = () => (
    <Box className="flex items-center">
      {navItems.map((item) => (
        <Box key={item.path} className="mx-4">
          <Typography
            component="a"
            href={item.path}
            className={`inline-flex items-center text-base ${
              activeLink === item.path ? "text-[#4CAF50]" : "text-[#4b5563]"
            } hover:text-[#4CAF50] transition-colors duration-300`}
            style={{ fontFamily: globalStyles.fonts.primary }}
            onClick={() => handleNavClick(item.path)}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
      {/* <Box
        component="button"
        onClick={toggleLanguage}
        className="ml-6 px-4 py-1.5 rounded-full text-semibold transition-all duration-300 border-0 outline-none focus:ring-2 focus:ring-[#4CAF50] focus:ring-offset-2 flex items-center hover:text-[#466b47]"
        style={{ color: globalStyles.colors.army_green, fontFamily: globalStyles.fonts.primary }}
      >
        {currentLanguage === "en" ? "العربية" : "English"}
        <img
          src={`${imageBaseUrl}globe_icon.png`}
          alt="Language"
          className="ml-2 h-5 w-5"
        />
      </Box> */}
    </Box>
  );

  // Mobile drawer content
  const renderDrawer = () => (
    <Box
      className={`w-[280px] p-2 transform transition-transform duration-300 ease-in-out ${
        mobileOpen
          ? "translate-x-0"
          : isRTL
          ? "translate-x-full"
          : "-translate-x-full"
      }`}
      role="presentation"
    >
      <Box className={`flex ${isRTL ? "justify-start" : "justify-end"} mb-2`}>
        <Box
          component="button"
          onClick={handleDrawerToggle}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
          aria-label="Close Menu"
        >
          <CloseIcon />
        </Box>
      </Box>
      <Box component="ul">
        {navItems.map((item, index) => (
          <Box
            component="li"
            key={item.path}
            className="mb-1 transition-opacity duration-300 ease-in-out"
            style={{ transitionDelay: `${index * 50}ms` }}
          >
            <Box className="px-0">
              <Typography
                component="a"
                href={item.path}
                className={`flex items-center px-1 py-0.5 text-base font-medium ${
                  activeLink === item.path ? "text-[#4CAF50]" : "text-[#4b5563]"
                } hover:text-[#4CAF50] transition-colors duration-300`}
                style={{ fontFamily: globalStyles.fonts.primary }}
                onClick={() => handleNavClick(item.path)}
              >
                {item.label}
              </Typography>
            </Box>
          </Box>
        ))}
        {/* Language toggle button in mobile view */}
        {/* <Box className="px-0 mt-4">
          <Box
            component="button"
            onClick={toggleLanguage}
            className="flex items-center text-base font-medium transition-colors duration-300"
            style={{ color: globalStyles.colors.army_green, fontFamily: globalStyles.fonts.primary }}
          >
            {currentLanguage === "en" ? "العربية" : "English"}
            <img
              src={`${imageBaseUrl}globe_icon.png`}
              alt="Language"
              className="ml-2 h-5 w-5"
            />
          </Box>
        </Box> */}
      </Box>
    </Box>
  );

  return (
    <Box dir={isRTL ? "rtl" : "ltr"}>
      <Box
        component="nav"
        className={`sticky top-0 bg-white shadow-none z-50 transition-shadow duration-300 ${
          isScrolled ? "shadow-sm" : ""
        }`}
      >
        <Box className="container mx-auto px-4">
          <Box className="flex justify-between items-center h-20 sm:h-24 px-4 sm:px-8 md:px-16 lg:px-[100px]">
            {/* Logo */}
            <Box className="flex items-center">
              <Box component="a" href="/srilanka" className="no-underline">
                <Box
                  component="img"
                  src={`${imageBaseUrl}logo.png`}
                  alt="UClean Sri Lanka"
                  className="h-10 sm:h-12 md:h-14 transition-all duration-300"
                />
              </Box>
            </Box>

            {/* Desktop Navigation */}
            <Box className="hidden lg:block">{renderDesktopNav()}</Box>

            {/* Mobile menu icon */}
            <Box className="block lg:hidden">
              <Box
                component="button"
                className={`p-2 text-[#4b5563] transition-transform duration-200 ${
                  mobileOpen ? "rotate-90" : "rotate-0"
                }`}
                aria-label="Open Menu"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Mobile drawer overlay and content */}
        <Box
          className={`fixed inset-0 z-50 transition-opacity duration-300 ${
            mobileOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
        >
          <Box
            className="absolute inset-0 bg-black transition-opacity duration-300"
            style={{ opacity: mobileOpen ? 0.5 : 0 }}
            onClick={handleDrawerToggle}
          />
          <Box
            className={`absolute inset-y-0 ${
              isRTL ? "right-0" : "left-0"
            } bg-white`}
            onClick={(e) => e.stopPropagation()}
          >
            {renderDrawer()}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NavbarSr;